import { handleError, hasError } from '@repo/shared/api/helpers'
import { PromptTemplateType } from '@repo/shared/dtos/organization'
import { Button } from '@repo/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@repo/ui/card'
import { useAsyncData } from '@repo/ui/dataProvider'
import { useT } from '@repo/ui/hooks'
import { Label } from '@repo/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@repo/ui/select'
import { TooltipWrapper } from '@repo/ui/tooltip'
import { Cfl } from '@repo/ui/utils'
import CodeMirror, { EditorView } from '@uiw/react-codemirror'
import { CheckCircleIcon } from 'lucide-react'
import { useEffect, useState } from 'react'
import CodeMirrorMerge from 'react-codemirror-merge'
import { Link } from 'react-router-dom'

import { SkeletonNote } from '@/components/skeletons/SkeletonNote'
import { api } from '@/main'
import { buildRoute, RouteNames } from '@/routes'
import { SudoContextOrganization, SudoContextPrompt } from '@/store/sudo'

export function PromptList({ organizations }: { organizations: SudoContextOrganization[] }) {
  const { tc } = useT()
  const [promptTemplateType, setPromptTemplateType] = useState<PromptTemplateType>()
  const [prompts, setPrompts] = useState<SudoContextPrompt>()
  const [isLoading, setIsLoading] = useState(false)
  const [organizationId, setOrganizationId] = useState<string>()
  useEffect(() => {
    const fetchPrompts = async () => {
      if (!promptTemplateType) return
      setPrompts(undefined)
      setOrganizationId(undefined)
      setIsLoading(true)
      const res = await api.sudo.prompts.get(promptTemplateType)
      setIsLoading(false)
      if (hasError(res)) return handleError(res)
      setPrompts(res)
    }
    // FIXME: should not fetch on useEffect
    void fetchPrompts()
  }, [promptTemplateType])

  const DefaultPrompt = CodeMirrorMerge.Original
  const OrganizationPrompt = CodeMirrorMerge.Modified

  const {
    data: [myOrganizations],
  } = useAsyncData([(state) => state.user.organizations])
  if (!myOrganizations) return null
  const myOrganizationIds = myOrganizations.map((org) => org.id)
  const isMember = myOrganizationIds.some((id) => id === organizationId)
  const organizationName = organizations.find((o) => o.id === organizationId)?.name

  return (
    <Card className="mb-6">
      <CardHeader>
        <CardTitle>Prompt Comparator</CardTitle>
        <CardDescription>
          {Object.keys(PromptTemplateType).length} prompt templates available
        </CardDescription>
        <CardDescription className="text-primary flex gap-4 pt-4">
          <div className="flex flex-col gap-2">
            <Label>1. Select a prompt template</Label>
            <Select
              onValueChange={(v) => setPromptTemplateType(v as PromptTemplateType)}
              value={promptTemplateType}
            >
              <SelectTrigger className="w-64">
                <SelectValue placeholder={tc('select', { cfl: true })} />
              </SelectTrigger>
              <SelectContent position="popper">
                {Object.values(PromptTemplateType).map((type) => (
                  <SelectItem key={type} value={type}>
                    {Cfl(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {prompts && (
            <div className="flex flex-col gap-2">
              <Label>2. Select an organization to compare</Label>
              {prompts.organizationPrompts.length > 0 ? (
                <Select onValueChange={setOrganizationId} value={organizationId}>
                  <SelectTrigger className="w-64">
                    <SelectValue
                      placeholder={`${tc('select', { cfl: true })} (${prompts.organizationPrompts.length})`}
                    />
                  </SelectTrigger>
                  <SelectContent position="popper">
                    {Object.values(prompts.organizationPrompts).map((item) => (
                      <SelectItem key={item.organizationId} value={item.organizationId}>
                        {organizations.find((o) => o.id === item.organizationId)?.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className="text-primary-500 flex h-10 items-center gap-2 text-base">
                  <CheckCircleIcon className="size-5" /> Nothing to compare. All organizations use
                  the default prompt.
                </div>
              )}
            </div>
          )}

          {organizationId && (
            <>
              {isMember && (
                <div className="flex items-end">
                  <TooltipWrapper content={`Open ${organizationName}`}>
                    <Button asChild variant="secondary">
                      <Link
                        target="_blank"
                        to={buildRoute(RouteNames.Organizations, [
                          organizationId,
                          `${RouteNames.Admin}?view=${promptTemplateType}`,
                        ])}
                      >
                        Edit Organization Prompt
                      </Link>
                    </Button>
                  </TooltipWrapper>
                </div>
              )}
            </>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="pb-6">
        {isLoading && <SkeletonNote />}
        {prompts && (
          <>
            {organizationId ? (
              <CodeMirrorMerge className="h-fit min-w-full">
                <DefaultPrompt
                  editable={false}
                  extensions={[EditorView.lineWrapping]}
                  readOnly
                  value={prompts.defaultPrompt}
                />
                <OrganizationPrompt
                  editable={false}
                  extensions={[EditorView.lineWrapping]}
                  readOnly
                  value={
                    prompts.organizationPrompts.find((p) => p.organizationId === organizationId)
                      ?.promptTemplate.promptText
                  }
                />
              </CodeMirrorMerge>
            ) : (
              <CodeMirror
                editable={false}
                extensions={[EditorView.lineWrapping]}
                maxWidth="50%"
                readOnly
                value={prompts.defaultPrompt}
              />
            )}
          </>
        )}
      </CardContent>
    </Card>
  )
}
